import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Chip,
  IconButton,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  Code as XmlIcon,
  Receipt as CdrIcon,
  FileDownload as ExcelIcon,
  Info as InfoIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Comprobante {
  numero: string;
  tipo: string;
  empresaRuc: string;
  fecha?: string;
  receptor?: {
    numeroDocumento: string;
    nombre?: string;
    razonSocial?: string;
  };
  subtotal?: number;
  igv?: number;
  total: number;
  moneda: string;
  estado: string;
  cdr?: {
    codigo: string;
    mensaje: string;
    observaciones?: string[];
    errores?: string[];
  };
}

export default function Comprobantes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isOperador, empresasAsignadas } = useAuth();
  
  const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingToSunat, setSendingToSunat] = useState<string | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<string | null>(null);
  const [downloadingXML, setDownloadingXML] = useState<string | null>(null);
  const [downloadingCDR, setDownloadingCDR] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [rowCount, setRowCount] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    numero: string;
    empresaRuc: string;
  }>({ open: false, numero: '', empresaRuc: '' });
  const [cdrDialog, setCdrDialog] = useState<{
    open: boolean;
    comprobante: Comprobante | null;
  }>({ open: false, comprobante: null });
  const [emailDialog, setEmailDialog] = useState<{
    open: boolean;
    numero: string;
    empresaRuc: string;
    email: string;
  }>({ open: false, numero: '', empresaRuc: '', email: '' });
  // Inicializar fechas con el día de hoy
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const [filters, setFilters] = useState({
    empresaRuc: '',
    tipo: '',
    numeroDocumento: '',
    nombre: '',
    estado: '',
    fechaInicio: today,
    fechaFin: new Date(), // Fecha actual con hora
  });

  // Cargar empresas al montar el componente
  React.useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      const data = await apiService.getEmpresas();
      let empresasList = data.empresas || [];
      
      // Si es operador, filtrar solo las empresas asignadas
      if (isOperador && empresasAsignadas.length > 0) {
        empresasList = empresasList.filter((empresa: any) => 
          empresasAsignadas.includes(empresa.ruc)
        );
      }
      
      setEmpresas(empresasList);
      // Seleccionar automáticamente la primera empresa
      if (empresasList.length > 0) {
        setFilters(prev => ({ ...prev, empresaRuc: empresasList[0].ruc }));
      }
    } catch (err) {
      console.error('Error al cargar empresas:', err);
    }
  };

  // Ejecutar búsqueda automáticamente cuando se selecciona una empresa
  React.useEffect(() => {
    if (filters.empresaRuc) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.empresaRuc]);

  const handleExportToExcel = () => {
    if (comprobantes.length === 0) {
      setError('No hay comprobantes para exportar');
      return;
    }

    // Preparar datos para Excel
    const excelData = comprobantes.map((c) => ({
      'Número': c.numero,
      'Tipo': c.tipo === '01' ? 'Factura' : 'Boleta',
      'Fecha Emisión': c.fecha ? new Date(c.fecha).toLocaleDateString('es-PE') : '',
      'RUC Empresa': c.empresaRuc,
      'Doc. Receptor': c.receptor?.numeroDocumento || '',
      'Receptor': c.receptor?.nombre || c.receptor?.razonSocial || '',
      'Subtotal': c.subtotal?.toFixed(2) || '0.00',
      'IGV': c.igv?.toFixed(2) || '0.00',
      'Total': c.total?.toFixed(2) || '0.00',
      'Moneda': c.moneda,
      'Estado': c.estado,
      'Código CDR': c.cdr?.codigo || '',
      'Mensaje CDR': c.cdr?.mensaje || '',
    }));

    // Crear libro de Excel
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comprobantes');

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 18 }, // Número
      { wch: 10 }, // Tipo
      { wch: 15 }, // Fecha
      { wch: 13 }, // RUC Empresa
      { wch: 15 }, // Doc. Receptor
      { wch: 30 }, // Receptor
      { wch: 12 }, // Subtotal
      { wch: 12 }, // IGV
      { wch: 12 }, // Total
      { wch: 8 },  // Moneda
      { wch: 12 }, // Estado
      { wch: 12 }, // Código CDR
      { wch: 50 }, // Mensaje CDR
    ];
    ws['!cols'] = colWidths;

    // Generar nombre de archivo con fecha
    const fechaActual = new Date().toISOString().split('T')[0];
    const fileName = `comprobantes_${fechaActual}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(wb, fileName);

    setSuccess(`Archivo ${fileName} descargado exitosamente`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSearch = async () => {
    if (!filters.empresaRuc) {
      setError('El RUC de la empresa es requerido');
      return;
    }

    setLoading(true);
    setError('');
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    
    try {
      // Ajustar fechaFin para incluir todo el día (hasta las 23:59:59.999)
      let fechaFinAjustada = filters.fechaFin;
      if (fechaFinAjustada) {
        fechaFinAjustada = new Date(fechaFinAjustada);
        fechaFinAjustada.setHours(23, 59, 59, 999);
      }

      const data = await apiService.listarComprobantes({
        empresaRuc: filters.empresaRuc,
        tipo: filters.tipo || undefined,
        estado: filters.estado || undefined,
        receptor: filters.numeroDocumento || undefined,
        nombre: filters.nombre || undefined,
        fechaInicio: filters.fechaInicio,
        fechaFin: fechaFinAjustada,
        limit: paginationModel.pageSize,
      });

      setComprobantes(data.comprobantes || []);
      setRowCount(data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al buscar comprobantes');
      setComprobantes([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos cuando cambia la paginación
  const loadPage = async (page: number, pageSize: number) => {
    if (!filters.empresaRuc) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Ajustar fechaFin para incluir todo el día
      let fechaFinAjustada = filters.fechaFin;
      if (fechaFinAjustada) {
        fechaFinAjustada = new Date(fechaFinAjustada);
        fechaFinAjustada.setHours(23, 59, 59, 999);
      }

      const data = await apiService.listarComprobantes({
        empresaRuc: filters.empresaRuc,
        tipo: filters.tipo || undefined,
        estado: filters.estado || undefined,
        receptor: filters.numeroDocumento || undefined,
        nombre: filters.nombre || undefined,
        fechaInicio: filters.fechaInicio,
        fechaFin: fechaFinAjustada,
        limit: pageSize,
        page: page,
      });

      setComprobantes(data.comprobantes || []);
      setRowCount(data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al cargar página');
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar datos cuando cambia la paginación
  React.useEffect(() => {
    if (filters.empresaRuc && rowCount > 0) {
      loadPage(paginationModel.page, paginationModel.pageSize);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleDownloadPDF = async (numero: string, empresaRuc: string) => {
    setDownloadingPDF(numero);
    setError('');
    try {
      const blob = await apiService.downloadPDF(numero, empresaRuc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`PDF ${numero} descargado exitosamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al descargar PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handleDownloadXML = async (numero: string, empresaRuc: string) => {
    setDownloadingXML(numero);
    setError('');
    try {
      const blob = await apiService.downloadXML(numero, empresaRuc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${numero}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`XML ${numero} descargado exitosamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al descargar XML');
    } finally {
      setDownloadingXML(null);
    }
  };

  const handleDownloadCDR = async (numero: string, empresaRuc: string) => {
    setDownloadingCDR(numero);
    setError('');
    try {
      const blob = await apiService.downloadCDR(numero, empresaRuc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `R-${empresaRuc}-${numero}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`CDR ${numero} descargado exitosamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error al descargar CDR');
    } finally {
      setDownloadingCDR(null);
    }
  };

  const handleSendToSunat = (numero: string, empresaRuc: string) => {
    setConfirmDialog({ open: true, numero, empresaRuc });
  };

  const confirmSendToSunat = async () => {
    const { numero, empresaRuc } = confirmDialog;
    setConfirmDialog({ open: false, numero: '', empresaRuc: '' });
    setSendingToSunat(numero);
    setError('');
    setSuccess('');

    try {
      await apiService.enviarComprobanteSunat(numero, empresaRuc);
      setSuccess(`Comprobante ${numero} enviado exitosamente a SUNAT`);
      setTimeout(() => setSuccess(''), 5000);
      
      // Actualizar el estado del comprobante
      await handleRefreshStatus(numero, empresaRuc);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || err.response?.data?.message || 'Error al enviar a SUNAT');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSendingToSunat(null);
    }
  };

  const handleRefreshStatus = async (numero: string, empresaRuc: string) => {
    try {
      const data = await apiService.getEstadoComprobante(numero, empresaRuc);
      // Actualizar el estado en la tabla
      setComprobantes((prev) =>
        prev.map((c) =>
          c.numero === numero ? { ...c, estado: data.estado } : c
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al consultar estado');
    }
  };

  const handleShowCDR = (comprobante: Comprobante) => {
    setCdrDialog({ open: true, comprobante });
  };

  const handleOpenEmailDialog = (numero: string, empresaRuc: string, comprobante: Comprobante) => {
    // El email se ingresará manualmente en el diálogo
    setEmailDialog({ open: true, numero, empresaRuc, email: '' });
  };

  const handleSendEmail = async () => {
    const { numero, empresaRuc, email } = emailDialog;
    
    if (!email || !email.includes('@')) {
      setError('Por favor ingrese un email válido');
      return;
    }

    setEmailDialog({ open: false, numero: '', empresaRuc: '', email: '' });
    setSendingEmail(numero);
    setError('');
    setSuccess('');

    try {
      await apiService.enviarEmail(numero, empresaRuc, email);
      setSuccess(`Email enviado exitosamente a ${email}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || err.response?.data?.message || 'Error al enviar email');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSendingEmail(null);
    }
  };

  const getCDRStatusColor = (codigo?: string) => {
    if (!codigo) return 'default';
    if (codigo === '0') return 'success';
    if (codigo.startsWith('0')) return 'warning'; // Aceptado con observaciones
    return 'error';
  };

  const getCDRStatusText = (codigo?: string) => {
    if (!codigo) return 'Sin CDR';
    if (codigo === '0') return 'Aceptado';
    if (codigo.startsWith('0')) return 'Aceptado con Observaciones';
    return 'Rechazado';
  };

  // Componente de tarjeta para vista móvil
  const ComprobanteCard = ({ comprobante }: { comprobante: Comprobante }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: 'primary.main' }}>
              {comprobante.numero}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {comprobante.fecha ? new Date(comprobante.fecha).toLocaleDateString('es-PE') : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip
              label={comprobante.tipo === '01' ? 'Factura' : 'Boleta'}
              color={comprobante.tipo === '01' ? 'primary' : 'secondary'}
              size="small"
            />
            <Chip
              label={comprobante.estado}
              color={
                comprobante.estado === 'ACEPTADO' ? 'success' :
                comprobante.estado === 'RECHAZADO' ? 'error' :
                comprobante.estado === 'ENVIADO' ? 'info' : 'default'
              }
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Receptor:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>
              {comprobante.receptor?.nombre || comprobante.receptor?.razonSocial || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Documento:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {comprobante.receptor?.numeroDocumento || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Total:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {comprobante.moneda} {comprobante.total?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Ver CDR">
            <span>
              <IconButton
                size="small"
                color="info"
                onClick={() => handleShowCDR(comprobante)}
                disabled={!comprobante.cdr}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Enviar Email">
            <span>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleOpenEmailDialog(comprobante.numero, comprobante.empresaRuc, comprobante)}
                disabled={sendingEmail === comprobante.numero}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {sendingEmail === comprobante.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <EmailIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="PDF">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadPDF(comprobante.numero, comprobante.empresaRuc)}
                disabled={downloadingPDF === comprobante.numero}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {downloadingPDF === comprobante.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <PdfIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="XML">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadXML(comprobante.numero, comprobante.empresaRuc)}
                disabled={downloadingXML === comprobante.numero}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {downloadingXML === comprobante.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <XmlIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="CDR">
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleDownloadCDR(comprobante.numero, comprobante.empresaRuc)}
                disabled={comprobante.estado !== 'ACEPTADO' || downloadingCDR === comprobante.numero}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {downloadingCDR === comprobante.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <CdrIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Enviar">
            <span>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleSendToSunat(comprobante.numero, comprobante.empresaRuc)}
                disabled={
                  comprobante.estado === 'ACEPTADO' || 
                  comprobante.estado === 'ENVIADO' ||
                  sendingToSunat === comprobante.numero
                }
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {sendingToSunat === comprobante.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Actualizar">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleRefreshStatus(comprobante.numero, comprobante.empresaRuc)}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  const columns: GridColDef[] = [
    { 
      field: 'numero', 
      headerName: 'Número', 
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value === '01' ? 'Factura' : 'Boleta'}
          color={params.value === '01' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    { 
      field: 'fecha', 
      headerName: 'Fecha', 
      width: 100,
      valueFormatter: (params) => {
        const value = params.value as string;
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-PE', { 
          day: '2-digit', 
          month: '2-digit',
          year: '2-digit'
        });
      },
    },
    { 
      field: 'receptorDocumento', 
      headerName: 'Doc.', 
      width: 100,
      valueGetter: (params) => params.row.receptor?.numeroDocumento || '',
    },
    { 
      field: 'receptorNombre', 
      headerName: 'Receptor', 
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.receptor?.nombre || params.row.receptor?.razonSocial || '',
    },
    { 
      field: 'total', 
      headerName: 'Total', 
      width: 90,
      valueFormatter: (params) => {
        const value = params.value as number;
        return `${value?.toFixed(2) || '0.00'}`;
      },
    },
    { 
      field: 'moneda', 
      headerName: 'Mon.', 
      width: 60,
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params) => {
        const colors: any = {
          PENDIENTE: 'default',
          ENVIADO: 'info',
          ACEPTADO: 'success',
          RECHAZADO: 'error',
        };
        return (
          <Chip
            label={params.value}
            color={colors[params.value] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 320,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
          <Tooltip title="Ver CDR">
            <span>
              <IconButton
                size="small"
                color="info"
                onClick={() => handleShowCDR(params.row)}
                disabled={!params.row.cdr}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Enviar Email">
            <span>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleOpenEmailDialog(params.row.numero, params.row.empresaRuc, params.row)}
                disabled={sendingEmail === params.row.numero}
              >
                {sendingEmail === params.row.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <EmailIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="PDF">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadPDF(params.row.numero, params.row.empresaRuc)}
                disabled={downloadingPDF === params.row.numero}
              >
                {downloadingPDF === params.row.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <PdfIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="XML">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadXML(params.row.numero, params.row.empresaRuc)}
                disabled={downloadingXML === params.row.numero}
              >
                {downloadingXML === params.row.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <XmlIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="CDR">
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleDownloadCDR(params.row.numero, params.row.empresaRuc)}
                disabled={params.row.estado !== 'ACEPTADO' || downloadingCDR === params.row.numero}
              >
                {downloadingCDR === params.row.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <CdrIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Enviar">
            <span>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleSendToSunat(params.row.numero, params.row.empresaRuc)}
                disabled={
                  params.row.estado === 'ACEPTADO' || 
                  params.row.estado === 'ENVIADO' ||
                  sendingToSunat === params.row.numero
                }
              >
                {sendingToSunat === params.row.numero ? (
                  <CircularProgress size={18} />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Actualizar">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleRefreshStatus(params.row.numero, params.row.empresaRuc)}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Comprobantes Electrónicos
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 4 } }}>
        Búsqueda y gestión de comprobantes electrónicos
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}>
            Filtros de Búsqueda
          </Typography>

          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 }
          }}>
            <TextField
              select
              label="Empresa"
              value={filters.empresaRuc}
              onChange={(e) => setFilters({ ...filters, empresaRuc: e.target.value })}
              size="small"
              required
              fullWidth
            >
              <MenuItem value="">Seleccione una empresa</MenuItem>
              {empresas.map((empresa) => (
                <MenuItem key={empresa.ruc} value={empresa.ruc}>
                  {empresa.ruc} - {empresa.razonSocial}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 1.5, sm: 2 }
            }}>
              <TextField
                select
                label="Tipo de Comprobante"
                value={filters.tipo}
                onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                size="small"
                fullWidth
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="01">Factura</MenuItem>
                <MenuItem value="03">Boleta</MenuItem>
              </TextField>

              <TextField
                select
                label="Estado"
                value={filters.estado}
                onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                size="small"
                fullWidth
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                <MenuItem value="ENVIADO">Enviado</MenuItem>
                <MenuItem value="ACEPTADO">Aceptado</MenuItem>
                <MenuItem value="RECHAZADO">Rechazado</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 1.5, sm: 2 }
            }}>
              <TextField
                label="Número de Documento"
                value={filters.numeroDocumento}
                onChange={(e) => setFilters({ ...filters, numeroDocumento: e.target.value })}
                size="small"
                fullWidth
                placeholder="DNI/RUC del receptor"
              />

              <TextField
                label="Nombre/Razón Social"
                value={filters.nombre}
                onChange={(e) => setFilters({ ...filters, nombre: e.target.value })}
                size="small"
                fullWidth
                placeholder="Nombre del receptor"
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 1.5, sm: 2 }
              }}>
                <DatePicker
                  label="Fecha Inicio"
                  value={filters.fechaInicio}
                  onChange={(date) => setFilters({ ...filters, fechaInicio: date || new Date() })}
                  slotProps={{ 
                    textField: { 
                      size: 'small', 
                      fullWidth: true 
                    } 
                  }}
                />

                <DatePicker
                  label="Fecha Fin"
                  value={filters.fechaFin}
                  onChange={(date) => setFilters({ ...filters, fechaFin: date || new Date() })}
                  slotProps={{ 
                    textField: { 
                      size: 'small', 
                      fullWidth: true 
                    } 
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 }, 
            mt: { xs: 2, sm: 3 }
          }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              fullWidth
              sx={{ 
                py: { xs: 1.2, sm: 1 },
                fontSize: { xs: '0.95rem', sm: '0.875rem' }
              }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<ExcelIcon />}
              onClick={handleExportToExcel}
              disabled={comprobantes.length === 0}
              color="success"
              fullWidth
              sx={{ 
                py: { xs: 1.2, sm: 1 },
                fontSize: { xs: '0.95rem', sm: '0.875rem' }
              }}
            >
              Exportar a Excel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Vista de tabla para desktop y cards para móvil */}
      {isMobile ? (
        // Vista móvil con cards
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : comprobantes.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  No se encontraron comprobantes
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              {comprobantes.map((comprobante) => (
                <ComprobanteCard key={comprobante.numero} comprobante={comprobante} />
              ))}
              
              {/* Paginación para móvil */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Mostrando {comprobantes.length} de {rowCount} registros
                      </Typography>
                      <TextField
                        select
                        size="small"
                        value={paginationModel.pageSize}
                        onChange={(e) => setPaginationModel({ ...paginationModel, pageSize: Number(e.target.value), page: 0 })}
                        sx={{ minWidth: 80 }}
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={paginationModel.page === 0}
                        onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}
                      >
                        Anterior
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Página {paginationModel.page + 1} de {Math.ceil(rowCount / paginationModel.pageSize)}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={(paginationModel.page + 1) * paginationModel.pageSize >= rowCount}
                        onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}
                      >
                        Siguiente
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      ) : (
        // Vista desktop con tabla
        <Card sx={{ overflow: 'hidden' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <DataGrid
              rows={comprobantes}
              columns={columns}
              getRowId={(row) => row.numero}
              loading={loading}
              autoHeight
              paginationMode="server"
              pageSizeOptions={[25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              disableColumnMenu
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                  py: 1.5,
                  px: 1,
                },
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  backgroundColor: 'rgba(30, 64, 175, 0.04)',
                  borderBottom: '2px solid rgba(30, 64, 175, 0.2)',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: 'rgba(30, 64, 175, 0.04)',
                  },
                },
                '& .MuiDataGrid-footerContainer': {
                  minHeight: '56px',
                  borderTop: '2px solid rgba(30, 64, 175, 0.1)',
                },
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Diálogo de información CDR */}
      <Dialog 
        open={cdrDialog.open} 
        onClose={() => setCdrDialog({ open: false, comprobante: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="info" />
            <Typography variant="h6">
              Respuesta de SUNAT - {cdrDialog.comprobante?.numero}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {cdrDialog.comprobante && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estado del Comprobante
                </Typography>
                <Chip
                  label={cdrDialog.comprobante.estado}
                  color={
                    cdrDialog.comprobante.estado === 'ACEPTADO' ? 'success' :
                    cdrDialog.comprobante.estado === 'RECHAZADO' ? 'error' :
                    cdrDialog.comprobante.estado === 'ENVIADO' ? 'info' : 'default'
                  }
                  size="medium"
                />
              </Box>

              {cdrDialog.comprobante.cdr ? (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Estado SUNAT
                    </Typography>
                    <Chip
                      label={getCDRStatusText(cdrDialog.comprobante.cdr.codigo)}
                      color={getCDRStatusColor(cdrDialog.comprobante.cdr.codigo)}
                      size="medium"
                      sx={{ fontSize: '1rem', py: 2.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Código de Respuesta
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      fontFamily: 'monospace', 
                      bgcolor: 'grey.100', 
                      p: 1.5, 
                      borderRadius: 1,
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      {cdrDialog.comprobante.cdr.codigo}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Mensaje de SUNAT
                    </Typography>
                    <Alert 
                      severity={
                        cdrDialog.comprobante.cdr.codigo === '0' ? 'success' :
                        cdrDialog.comprobante.cdr.codigo?.startsWith('0') ? 'warning' : 'error'
                      }
                      sx={{ fontSize: '1rem' }}
                    >
                      {cdrDialog.comprobante.cdr.mensaje}
                    </Alert>
                  </Box>

                  {cdrDialog.comprobante.cdr.codigo?.startsWith('0') && cdrDialog.comprobante.cdr.codigo !== '0' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ℹ️ Comprobante Aceptado con Observaciones
                      </Typography>
                      <Typography variant="body2">
                        El comprobante fue aceptado por SUNAT pero tiene observaciones que debes revisar. 
                        Aunque es válido, se recomienda corregir las observaciones para futuros comprobantes.
                      </Typography>
                      {cdrDialog.comprobante.cdr.observaciones && cdrDialog.comprobante.cdr.observaciones.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Observaciones:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {cdrDialog.comprobante.cdr.observaciones.map((obs, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">{obs}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    </Alert>
                  )}

                  {cdrDialog.comprobante.cdr.codigo && !cdrDialog.comprobante.cdr.codigo.startsWith('0') && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ❌ Comprobante Rechazado
                      </Typography>
                      <Typography variant="body2">
                        El comprobante fue rechazado por SUNAT. Debes corregir los errores indicados 
                        y generar un nuevo comprobante.
                      </Typography>
                      {cdrDialog.comprobante.cdr.errores && cdrDialog.comprobante.cdr.errores.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Errores a corregir:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {cdrDialog.comprobante.cdr.errores.map((error, idx) => (
                              <li key={idx}>
                                <Typography variant="body2" sx={{ color: 'error.dark' }}>{error}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="warning">
                  Este comprobante aún no ha sido enviado a SUNAT o no tiene respuesta CDR disponible.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCdrDialog({ open: false, comprobante: null })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para envío a SUNAT */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, numero: '', empresaRuc: '' })}>
        <DialogTitle>Confirmar Envío a SUNAT</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea enviar el comprobante <strong>{confirmDialog.numero}</strong> a SUNAT?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Esta acción enviará el comprobante al servicio web de SUNAT para su validación y registro.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, numero: '', empresaRuc: '' })}>
            Cancelar
          </Button>
          <Button onClick={confirmSendToSunat} variant="contained" color="primary">
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para enviar email */}
      <Dialog 
        open={emailDialog.open} 
        onClose={() => setEmailDialog({ open: false, numero: '', empresaRuc: '', email: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="secondary" />
            <Typography variant="h6">
              Enviar Comprobante por Email
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Comprobante: <strong>{emailDialog.numero}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Se enviarán los archivos PDF, XML y CDR (si está disponible) al destinatario.
            </Typography>
            <TextField
              fullWidth
              label="Email del Destinatario"
              type="email"
              value={emailDialog.email}
              onChange={(e) => setEmailDialog({ ...emailDialog, email: e.target.value })}
              placeholder="ejemplo@correo.com"
              autoFocus
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog({ open: false, numero: '', empresaRuc: '', email: '' })}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="secondary"
            startIcon={<EmailIcon />}
            disabled={!emailDialog.email || !emailDialog.email.includes('@')}
          >
            Enviar Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
